/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {HealthEvent, HealthEvents} from '@ion/core/src/types/entities';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {CopyIcon} from '@ion/dls/src/icons/CopyIcon';
import {ViewRecordIcon} from '@ion/dls/src/icons/ViewRecordIcon';
import {Color} from '@ion/dls/src/types/types';

export interface HealthEventsRowEditorProps {
  healthEvent: HealthEvent | HealthEvents ;
  diffDetails: Function;
}

/**
 * Renders a menu displaying possible actions for a user to access at the end of each Failsafe Audit log row in the grid
 */
export const HealthEventsRowEditor = ({healthEvent, diffDetails}: HealthEventsRowEditorProps) => {
  const dispatch    = useDispatch();
  const cellMenuRef = useRef(null);

  const closeMenu = useCallback(function() {
    cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
  }, []);

  const onView = useCallback(function() {
    diffDetails(healthEvent, true);
  }, [diffDetails, healthEvent]);

  // Handles when a user wants to clone a Failsafe Audit log
  const onClone = useCallback(function() {
    navigator.clipboard.writeText(safeJSONStringify(healthEvent));
    closeMenu();
  }, [healthEvent]);

  return (
    <CellMenu ref={cellMenuRef} menuItems={
      <div>
        <CellMenuItem icon={<ViewRecordIcon />}
          onClick={onView}
          label={translate('admin-assets:grid.menu.view')} />
        <CellMenuItem icon={<CopyIcon color={Color.grey3}/>}
          onClick={onClone}
          label={translate('common:clone')} />
      </div>
    } />
  );
};

export default React.memo(HealthEventsRowEditor);
